import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../redux/store";
import { fetchUserData, logout } from "../redux/authSlice";
import { useNavigate } from "react-router-dom";
import { Modal, Fade, Typography } from "@mui/material";
import { CheckCircle, Share } from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import {
  Bell, Camera, Shield, ShieldCheck, Package, RefreshCw,
  RotateCcw, CreditCard, Headphones, Pencil, Store,
  Home, BarChart2, Settings, LogOut, Plus, Copy, Users,
  ChevronRight, Wallet, LayoutDashboard, ClipboardList,
  UserCheck, MapPin, Star, TrendingUp, Cloud,
} from "lucide-react";

/* ─── Palette — Heartlas (cream / gold / red) ──────────────── */
const HEART = {
  bg:        "#FBF6EE",
  panelBg:   "#FFFFFF",
  panel2:    "#FFF9EF",
  dark:      "#1B0E06",
  textMuted: "#7A6653",
  textFaint: "#A6947F",
  red:       "#C8202E",
  redDark:   "#9E1521",
  redSoft:   "#FBE7E9",
  gold:      "#F2A93E",
  goldDark:  "#D4900F",
  goldSoft:  "#FCEFD4",
  border:    "#EEDFC4",
  divider:   "#F3E6CE",
};

// kept the old variable names so the JSX below barely had to change
const WHITE   = HEART.panelBg;
const OFF     = HEART.dark;
const BG      = HEART.bg;
const SURFACE = HEART.panelBg;
const SURF2   = HEART.panel2;
const DARK    = HEART.dark;
const MUTED   = HEART.textMuted;
const BORDER  = HEART.border;
const BORDER2 = HEART.border;
const SUBTLE  = HEART.textFaint;

/* ─── Font injection ────────────────────────────────────── */
const FONT_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600;700&family=Montserrat:wght@300;400;500;600;700&display=swap');
`;

const maskName = (name: string) => {
  if (!name) return "";
  if (name.length <= 2) return name;
  return name.slice(0, 2) + "·".repeat(name.length - 2);
};
const maskEmail = (email: string) => {
  if (!email) return "";
  const [u, d] = email.split("@");
  if (!d) return email;
  return `${u.slice(0, 3)}···@${d}`;
};

const Profile: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { user, authLoaded } = useSelector((s: RootState) => s.auth);
  const userId = user?.userId;

  const [copied, setCopied]                 = useState(false);
  const [instantRefunds, setInstantRefunds] = useState(false);

  useEffect(() => { dispatch(fetchUserData()); }, [dispatch]);

  // never let a signed-out user sit on this page — bounce to home immediately
  useEffect(() => {
    if (authLoaded && !user) {
      navigate("/");
    }
  }, [authLoaded, user, navigate]);

  const profileCompletion = () => {
    const fields = ["firstName","lastName","emailAddress","phoneNumber","username","profilePicture","userAge","userAddress","verifyEmail"];
    const filled = fields.filter(f => {
      const v = f === "userAddress"
        ? user?.userAddress?.city && user.userAddress.streetAddress
        : user?.[f as keyof typeof user];
      return !!v;
    });
    return Math.round((filled.length / fields.length) * 100);
  };

  const isVerified = user?.isVerified === true;
  const completion = profileCompletion();
  const firstName  = maskName(user?.firstName || "First");
  const lastName   = maskName(user?.lastName  || "Last");
  const email      = maskEmail(user?.emailAddress || user?.email || "email@example.com");
  const photo      = user?.profilePicture || user?.photoURL || null;
  const initials   = `${user?.firstName?.[0]||""}${user?.lastName?.[0]||""}`.toUpperCase() || "U";

  /* ── still resolving auth / redirecting a signed-out user ── */
  if (!authLoaded || !user) {
    return (
      <div style={M.root}>
        <style>{FONT_CSS + RESP_CSS}</style>
      </div>
    );
  }

  /* ── Referral section ──────────────────────────────────── */
  const ReferralBlock = ({ dark }: { dark?: boolean }) => !user?.username ? null : (
    <div style={dark ? D.referralCard : M.referralCard}>
      <div style={{ fontSize:10, color:MUTED, letterSpacing:"0.2em", marginBottom:10 }}>REFER & EARN</div>
      <div style={{ fontWeight:400, fontSize:18, color:DARK, marginBottom:6, fontFamily:"'Cormorant Garamond', serif" }}>Invite friends, earn €10 each</div>
      <p style={{ color:MUTED, fontSize:12, marginBottom:14, lineHeight:"1.6" }}>
        Share your link — you both earn €10 on their first booking.
      </p>
      <div style={{ display:"flex", gap:8, marginBottom:10 }}>
        <input readOnly value={`heartlas.com/ref/${user.username}`}
          style={{ flex:1, borderRadius:10, padding:"10px 12px", border:`1px solid ${BORDER}`, fontSize:11, color:MUTED, outline:"none", background:HEART.goldSoft, fontFamily:"monospace" }}/>
        <button style={{ background:`linear-gradient(135deg, ${HEART.red}, ${HEART.redDark})`, color:"#fff", fontWeight:600, border:"none", borderRadius:10, padding:"10px 18px", cursor:"pointer", display:"flex", alignItems:"center", gap:6, fontSize:11, letterSpacing:"0.1em", fontFamily:"'Montserrat', sans-serif" }}
          onClick={async () => { await navigator.clipboard.writeText(`https://heartlas.com/ref/${user.username}`); setCopied(true); setTimeout(()=>setCopied(false),2000); }}>
          <Copy size={13}/> COPY
        </button>
      </div>
      <button style={{ width:"100%", background:"transparent", color:DARK, fontWeight:500, border:`1px solid ${BORDER}`, borderRadius:10, padding:"12px 0", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8, fontSize:12, letterSpacing:"0.1em", fontFamily:"'Montserrat', sans-serif" }}
        onClick={async () => { if(navigator.share) await navigator.share({ title:"Come earn rewards", text:"Earn rewards!", url:`https://heartlas.com/ref/${user.username}` }); else alert("Sharing not supported."); }}>
        <Users size={15}/> INVITE FRIENDS
      </button>
      <Modal open={copied} onClose={()=>setCopied(false)}>
        <Fade in={copied}>
          <div style={{ position:"absolute", top:"50%", left:"50%", transform:"translate(-50%,-50%)", background:"#fff", borderRadius:16, padding:"50px 40px", outline:"none", textAlign:"center", border:`1px solid ${HEART.gold}`, boxShadow:"0 20px 50px rgba(27,14,6,0.18)" }}>
            <AnimatePresence>
              {copied && (
                <motion.div initial={{scale:0,opacity:0}} animate={{scale:1.2,opacity:1}} exit={{scale:0,opacity:0}} transition={{duration:0.4}}>
                  <CheckCircle style={{fontSize:70,color:HEART.red}}/>
                  <Typography variant="h6" sx={{color:DARK,mt:1,fontFamily:"'Cormorant Garamond', serif",letterSpacing:"0.1em"}}>Link Copied</Typography>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </Fade>
      </Modal>
    </div>
  );

  return (
    <div style={M.root}>
      <style>{FONT_CSS + RESP_CSS}</style>

      {/* ══════════ MOBILE ══════════ */}
      <div className="mobile-only" style={M.mobileWrapper}>
        {/* Header */}
        <div style={M.header}>
          <div style={M.topBar}>
            <span style={M.pageTitle}>Profile</span>
            <div style={{ display:"flex", alignItems:"center", gap:10 }}>
              {(user?.isAdmin || user?.isDirector) && (
                <button
                  onClick={() => window.open(`/OperationsManagement/${userId}`, "_blank")}
                  style={M.logoBtn}
                  aria-label="Operations"
                >
                  <Settings size={16} color={DARK}/>
                </button>
              )}
              <button style={M.bellBtn} onClick={()=>navigate("/Notifications")}><Bell size={18} color={DARK}/></button>
            </div>
          </div>
          <div style={M.avatarSection}>
            <div style={M.avatarOuter}>
              <div style={{position:"relative"}}>
                {photo ? <img src={photo} alt="avatar" style={M.avatar}/> : <div style={M.avatarPlaceholder}><span style={M.avatarInitials}>{initials}</span></div>}
                <button style={M.cameraIcon} onClick={()=>navigate(`/EditProfile/${userId}`)}><Camera size={11} color={DARK}/></button>
                {isVerified && <div style={M.verifiedBadge}><ShieldCheck size={12} color={HEART.red}/></div>}
              </div>
            </div>
            <div style={{flex:1}}>
              <div style={M.userName}>{firstName} {lastName}</div>
              <div style={M.userEmail}>{email}</div>
              <div style={isVerified ? M.pillVerified : M.pillPending}>
                {isVerified ? <ShieldCheck size={11} color={HEART.red}/> : <Shield size={11} color={MUTED}/>}
                <span style={{fontSize:9,fontWeight:600,color:isVerified?HEART.red:MUTED,letterSpacing:"0.12em"}}>{isVerified?"VERIFIED":"UNVERIFIED"}</span>
              </div>
              <div style={{color:DARK,fontSize:11,fontWeight:500,marginTop:4,letterSpacing:"0.05em"}}>{completion}% Complete</div>
            </div>
          </div>
          <div style={M.refundCard}>
            <div style={{flex:1,marginRight:12}}>
              <div style={M.refundTitle}>Instant Refunds</div>
              <div style={M.refundSub}>Direct refunds to your wallet</div>
            </div>
            <label style={{cursor:"pointer"}}>
              <input type="checkbox" checked={instantRefunds} onChange={()=>setInstantRefunds(v=>!v)} style={{display:"none"}}/>
              <div style={{width:46,height:26,borderRadius:999,background:instantRefunds?`linear-gradient(135deg, ${HEART.red}, ${HEART.redDark})`:HEART.goldSoft,position:"relative",transition:"background 0.2s",display:"flex",alignItems:"center",border:`1px solid ${instantRefunds?HEART.red:BORDER2}`}}>
                <div style={{width:20,height:20,borderRadius:"50%",backgroundColor:"#fff",position:"absolute",transition:"transform 0.2s",transform:instantRefunds?"translateX(22px)":"translateX(2px)",boxShadow:"0 1px 3px rgba(0,0,0,.2)"}}/>
              </div>
            </label>
          </div>
        </div>

        {/* Body */}
        <div style={M.body}>
          <div style={M.bannerRow}>
            <button style={M.bannerCard} onClick={()=>navigate(`/CommerceOnboarding/${userId}`)}>
              <div style={M.bannerIcon}><TrendingUp size={18} color={HEART.red}/></div>
              <div style={{textAlign:"left"}}><div style={M.bannerSub}>No SHARE points?</div><div style={M.bannerAction}>Earn Today →</div></div>
            </button>
            <button style={M.bannerCard} onClick={()=>navigate("/Reorder")}>
              <div style={M.bannerIcon}><Star size={18} color={HEART.red}/></div>
              <div style={{textAlign:"left"}}><div style={M.bannerSub}>No savings yet?</div><div style={M.bannerAction}>Start Shopping!</div></div>
            </button>
          </div>

          <div style={M.actionRow}>
            <MobActionCard icon={<Package size={22} color={HEART.red}/>}     label="My Orders"  highlight onClick={()=>navigate("/MetaClientDashboard")}/>
            <MobActionCard icon={<RefreshCw size={22} color={MUTED}/>}   label="Returns"             onClick={()=>navigate("/Returns")}/>
            <MobActionCard icon={<RotateCcw size={22} color={MUTED}/>}   label="Buy Again"           onClick={()=>navigate("/Reorder")}/>
          </div>

          <div style={M.sectionTitle}>MY ADDRESSES</div>
          <div style={M.addressCard}>
            <p style={{color:MUTED,fontSize:13,marginBottom:14}}>Haven't added a delivery address yet?</p>
            <button style={M.addAddressBtn} onClick={()=>navigate(`/EditProfile/${userId}`)}>
              <Plus size={14} color="#fff" style={{marginRight:6}}/> Add a New Address
            </button>
          </div>

          <MobListItem icon={<CreditCard size={20} color={DARK}/>}   label="Wallet & Saved Cards"         onClick={()=>navigate(`/wallet/${userId}`)}/>
          <MobListItem icon={<Headphones size={20} color={DARK}/>}   label="Customer Service"              onClick={()=>navigate("/CustomerSupport")} right={<div style={M.supportBtn}><Headphones size={16} color="#fff"/></div>}/>
          <MobListItem icon={<Pencil size={20} color={DARK}/>}       label="Edit Profile"                  onClick={()=>navigate(`/EditProfile/${userId}`)}/>
          <MobListItem icon={<ShieldCheck size={20} color={DARK}/>}  label="Verify Profile"                onClick={()=>navigate("/VerifyProfile")}/>
          <MobListItem icon={<Store size={20} color={DARK}/>}        label="Start Earning"                onClick={()=>navigate(`/onboarding/${userId}`)}/>
          <MobListItem icon={<LayoutDashboard size={20} color={DARK}/>} label="Store Central"             onClick={()=>navigate("/store")}/>
          <MobListItem icon={<Settings size={20} color={DARK}/>}     label="Settings & Privacy"            onClick={()=>navigate(`/ProfileSettings/${userId}`)}/>
          <MobListItem icon={<Bell size={20} color={DARK}/>}         label="Notifications"                 onClick={()=>navigate("/Notifications")}/>

          <ReferralBlock/>

          <button style={M.logoutBtn} onClick={async () => { await dispatch(logout()).unwrap(); navigate("/"); }}>
            <LogOut size={18} color="#fff" style={{marginRight:10}}/> LOG OUT
          </button>
        </div>
      </div>

      {/* ══════════ DESKTOP ══════════ */}
      <div className="desktop-only" style={D.root}>
        <DeskNav userId={userId} user={user} navigate={navigate} handleDashboard={()=>navigate(`/GlamourManagementDashboard/${userId}`)}/>

        {/* Hero banner */}
        <div style={D.heroBanner}>
          <div style={D.heroLeft}>
            <div style={D.heroAvatarOuter}>
              <div style={{position:"relative"}}>
                {photo ? <img src={photo} alt="avatar" style={D.heroAvatar}/> : <div style={D.heroAvatarPlaceholder}><span style={D.heroInitials}>{initials}</span></div>}
                <button style={D.heroCamBtn} onClick={()=>navigate(`/EditProfile/${userId}`)}><Camera size={14} color={DARK}/></button>
                {isVerified && <div style={D.heroVerBadge}><ShieldCheck size={14} color={HEART.red}/></div>}
              </div>
            </div>
            <div>
              <div style={D.heroName}>{firstName} {lastName}</div>
              <div style={D.heroEmail}>{email}</div>
              <div style={{display:"flex",alignItems:"center",gap:12,marginTop:12}}>
                <div style={isVerified ? M.pillVerified : M.pillPending}>
                  {isVerified ? <ShieldCheck size={11} color={HEART.red}/> : <Shield size={11} color={MUTED}/>}
                  <span style={{fontSize:9,fontWeight:600,color:isVerified?HEART.red:MUTED,letterSpacing:"0.12em"}}>{isVerified?"VERIFIED":"UNVERIFIED"}</span>
                </div>
                <span style={{color:DARK,fontSize:11,fontWeight:500,letterSpacing:"0.05em"}}>{completion}% Complete</span>
              </div>
            </div>
          </div>

          <div style={D.heroRight}>
            <div style={D.heroToggleCard}>
              <div>
                <div style={{color:DARK,fontWeight:600,fontSize:13,marginBottom:3,letterSpacing:"0.03em"}}>Instant Refunds</div>
                <div style={{color:MUTED,fontSize:11}}>Direct refunds to your wallet</div>
              </div>
              <label style={{cursor:"pointer"}}>
                <input type="checkbox" checked={instantRefunds} onChange={()=>setInstantRefunds(v=>!v)} style={{display:"none"}}/>
                <div style={{width:52,height:28,borderRadius:999,background:instantRefunds?`linear-gradient(135deg, ${HEART.red}, ${HEART.redDark})`:HEART.goldSoft,position:"relative",transition:"background 0.2s",display:"flex",alignItems:"center",border:`1px solid ${instantRefunds?HEART.red:BORDER2}`}}>
                  <div style={{width:22,height:22,borderRadius:"50%",backgroundColor:"#fff",position:"absolute",transition:"transform 0.2s",transform:instantRefunds?"translateX(26px)":"translateX(3px)",boxShadow:"0 1px 3px rgba(0,0,0,.2)"}}/>
                </div>
              </label>
            </div>
            <div style={D.heroStats}>
              <HeroStat icon={<Package size={18} color={HEART.red}/>}     label="Orders"  onClick={()=>navigate(`/clientproductorders/${userId}`)}/>
              <HeroStat icon={<Wallet size={18} color={DARK}/>}        label="Wallet"  onClick={()=>navigate(`/wallet/${userId}`)}/>
              <HeroStat icon={<Star size={18} color={HEART.red}/>}        label="Points"  onClick={()=>navigate(`/onboarding/${userId}`)}/>
            </div>
          </div>
        </div>

        {/* 3-column grid */}
        <div style={D.grid}>

          {/* COL 1 — Account */}
          <div style={D.col}>
            <div style={D.colHeader}><span style={D.colTitle}>ACCOUNT</span></div>
            <DeskListItem icon={<Pencil size={15}/>}     label="Edit Profile"       sub="Update your info"       onClick={()=>navigate(`/EditProfile/${userId}`)}/>
            <DeskListItem icon={<UserCheck size={15}/>}  label="Verify Profile"     sub="Complete verification"  onClick={()=>navigate("/VerifyProfile")}/>
            <DeskListItem icon={<MapPin size={15}/>}     label="My Addresses"       sub="Manage delivery spots"  onClick={()=>navigate(`/EditProfile/${userId}`)} badge="Add"/>
            <DeskListItem icon={<Settings size={15}/>}   label="Settings & Privacy" sub="Security & preferences" onClick={()=>navigate(`/ProfileSettings/${userId}`)}/>
            <DeskListItem icon={<Bell size={15}/>}       label="Notifications"      sub="Alerts & updates"       onClick={()=>navigate("/Notifications")}/>
            <button onClick={async()=>{ await dispatch(logout()).unwrap(); navigate("/"); }} style={D.logoutBtn}><LogOut size={15}/><span>LOG OUT</span></button>
          </div>

          {/* COL 2 — Commerce */}
          <div style={D.col}>
            <div style={D.colHeader}><span style={D.colTitle}>COMMERCE</span></div>
            <DeskListItem icon={<Store size={15}/>}      label="Start Earning"     sub="List your products"     onClick={()=>navigate(`/onboarding/${userId}`)}/>
            <DeskListItem icon={<Package size={15}/>}    label="My Orders"          sub="Track & manage orders"  onClick={()=>navigate("/ClientDashboard")} badge="New"/>
            <DeskListItem icon={<RefreshCw size={15}/>}  label="Returns"            sub="Start a return"         onClick={()=>navigate("/Returns")}/>
            <DeskListItem icon={<RotateCcw size={15}/>}  label="Buy Again"          sub="Reorder favourites"     onClick={()=>navigate("/Reorder")}/>
            <DeskListItem icon={<CreditCard size={15}/>} label="Wallet & Cards"     sub="Payments & balance"     onClick={()=>navigate("/wallet")}/>
            <DeskListItem icon={<Headphones size={15}/>} label="Customer Service"   sub="Get help instantly"     onClick={()=>navigate("/support")} badge="Chat"/>
          </div>

          {/* COL 3 — Hosting + Referral */}
          <div style={D.col}>
            <div style={D.colHeader}><span style={D.colTitle}>DASHBOARD</span></div>
            <DeskListItem icon={<BarChart2 size={15}/>}     label="Commerce Dashboard" sub="Manage your business" onClick={()=>navigate(`/onboarding/${userId}`)}/>
            <DeskListItem icon={<Store size={15}/>}         label="Store Central"      sub="Products, orders & more" onClick={()=>navigate("/store")}/>
            
            <div style={{marginTop:24}}>
              <div style={D.colHeader}><span style={D.colTitle}>REFERRALS</span></div>
              <ReferralBlock dark/>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default Profile;

/* ── Desktop Nav (no logo, no brand name) ──────────────────── */
const DeskNav = ({ userId, user, navigate, handleDashboard }: any) => (
  <nav style={D.nav}>
    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
      <NavLink icon={<LayoutDashboard size={15}/>} label="Dashboard"  onClick={handleDashboard}/>
      <NavLink icon={<Package size={15}/>}         label="My Orders"  onClick={()=>navigate(`/clientdashboard/${userId}`)}/>
      <NavLink icon={<Store size={15}/>}           label="Start Earning" onClick={()=>navigate(`/onboarding/${userId}`)}/>
    </div>
    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
      <button style={D.navIconBtn} onClick={()=>navigate("/Notifications")}><Bell size={18} color={MUTED}/></button>
      <button style={D.navIconBtn} onClick={()=>navigate(`/ProfileSettings/${userId}`)}><Settings size={18} color={MUTED}/></button>
      {(user?.isAdmin || user?.isDirector) && (
        <button style={D.navIconBtn} onClick={()=>window.open(`/OperationsManagement/${userId}`,"_blank")}><Cloud size={18} color={MUTED}/></button>
      )}
    </div>
  </nav>
);

/* ── Sub-components ─────────────────────────────────────────── */
const NavLink = ({ icon, label, onClick }: { icon: React.ReactNode; label: string; onClick: () => void }) => (
  <button onClick={onClick}
    style={{ background:"none", border:"none", cursor:"pointer", display:"flex", alignItems:"center", gap:6, color:MUTED, fontSize:12, fontWeight:600, padding:"8px 16px", borderRadius:10, fontFamily:"'Montserrat', sans-serif", letterSpacing:"0.05em", transition:"color 0.2s, background .2s" }}
    onMouseEnter={e=>{(e.currentTarget as HTMLButtonElement).style.color=HEART.red;(e.currentTarget as HTMLButtonElement).style.background=HEART.goldSoft;}}
    onMouseLeave={e=>{(e.currentTarget as HTMLButtonElement).style.color=MUTED;(e.currentTarget as HTMLButtonElement).style.background="transparent";}}>
    {icon}{label}
  </button>
);

const HeroStat = ({ icon, label, onClick }: { icon: React.ReactNode; label: string; onClick: () => void }) => (
  <button onClick={onClick}
    style={{ background:HEART.goldSoft, border:`1px solid ${BORDER}`, borderRadius:14, padding:"16px 20px", cursor:"pointer", display:"flex", flexDirection:"column", gap:8, alignItems:"center", flex:1, transition:"border-color 0.2s" }}
    onMouseEnter={e=>{(e.currentTarget as HTMLButtonElement).style.borderColor=HEART.gold;}}
    onMouseLeave={e=>{(e.currentTarget as HTMLButtonElement).style.borderColor=BORDER;}}>
    {icon}
    <span style={{ color:MUTED, fontSize:10, fontWeight:600, letterSpacing:"0.12em", fontFamily:"'Montserrat', sans-serif" }}>{label.toUpperCase()}</span>
  </button>
);

const DeskListItem = ({ icon, label, sub, onClick, badge }: { icon: React.ReactNode; label: string; sub: string; onClick: () => void; badge?: string }) => (
  <button onClick={onClick} style={D.listItem}
    onMouseEnter={e=>{const b=e.currentTarget as HTMLButtonElement;b.style.borderColor=HEART.gold;}}
    onMouseLeave={e=>{const b=e.currentTarget as HTMLButtonElement;b.style.borderColor=BORDER;}}>
    <div style={D.listIconWrap}>{icon}</div>
    <div style={{ flex:1, textAlign:"left" }}>
      <div style={{ fontSize:13, fontWeight:600, color:DARK }}>{label}</div>
      <div style={{ fontSize:11, color:MUTED, marginTop:2 }}>{sub}</div>
    </div>
    {badge
      ? <span style={{ background:HEART.red, color:"#fff", fontSize:9, fontWeight:700, borderRadius:999, padding:"3px 10px", letterSpacing:"0.1em" }}>{badge.toUpperCase()}</span>
      : <ChevronRight size={15} color={SUBTLE}/>
    }
  </button>
);

const MobActionCard = ({ icon, label, onClick, highlight }: { icon: React.ReactNode; label: string; onClick: () => void; highlight?: boolean }) => (
  <button onClick={onClick} style={{ flex:1, background:SURF2, borderRadius:14, paddingTop:22, paddingBottom:22, display:"flex", flexDirection:"column", alignItems:"center", gap:8, border:`1px solid ${highlight?HEART.gold:BORDER}`, cursor:"pointer" }}>
    {icon}
    <span style={{ fontSize:10, fontWeight:600, color:highlight?HEART.red:MUTED, textAlign:"center", letterSpacing:"0.08em", fontFamily:"'Montserrat', sans-serif" }}>{label.toUpperCase()}</span>
  </button>
);

const MobListItem = ({ icon, label, onClick, right }: { icon: React.ReactNode; label: string; onClick: () => void; right?: React.ReactNode }) => (
  <button onClick={onClick} style={M.listItem}>
    <div style={M.listIconWrap}>{icon}</div>
    <span style={M.listLabel}>{label}</span>
    {right ?? <ChevronRight size={18} color={SUBTLE} style={{ marginLeft:"auto" }}/>}
  </button>
);

/* ── CSS strings ────────────────────────────────────────────── */
const RESP_CSS = `
  .desktop-only{display:none}
  .mobile-only{display:block}
  @media(min-width:900px){
    .desktop-only{display:block!important}
    .mobile-only{display:none!important}
  }
`;

/* ── Mobile styles ──────────────────────────────────────────── */
const M: { [k: string]: React.CSSProperties } = {
  root:          { minHeight:"100vh", backgroundColor:BG, display:"flex", justifyContent:"center", fontFamily:"'Montserrat', 'Cormorant Garamond', sans-serif" },
  mobileWrapper: { width:"100%", maxWidth:500 },
  header:        { position:"relative", overflow:"hidden", background:SURFACE, padding:"0 20px 26px", borderBottom:`1px solid ${BORDER}` },
  topBar:        { display:"flex", flexDirection:"row", alignItems:"center", justifyContent:"space-between", paddingTop:54, marginBottom:24 },
  pageTitle:     { color:DARK, fontSize:24, fontWeight:600, fontFamily:"'Cormorant Garamond', serif", letterSpacing:"0.12em" },
  logoBtn:       { width:36, height:36, borderRadius:10, background:HEART.goldSoft, border:`1px solid ${BORDER}`, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", padding:0 },
  bellBtn:       { width:36, height:36, borderRadius:10, backgroundColor:HEART.goldSoft, border:`1px solid ${BORDER}`, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" },
  avatarSection: { display:"flex", alignItems:"center", gap:18, marginBottom:22 },
  avatarOuter:   { position:"relative", width:84, height:84, display:"flex", alignItems:"center", justifyContent:"center" },
  avatar:            { width:76, height:76, borderRadius:"50%", border:`1.5px solid ${HEART.gold}`, objectFit:"cover", display:"block" },
  avatarPlaceholder: { width:76, height:76, borderRadius:"50%", backgroundColor:HEART.goldSoft, border:`1.5px solid ${HEART.gold}`, display:"flex", alignItems:"center", justifyContent:"center" },
  avatarInitials:    { color:HEART.red, fontSize:22, fontWeight:600, fontFamily:"'Cormorant Garamond', serif" },
  cameraIcon:    { position:"absolute", bottom:2, right:2, backgroundColor:"#fff", borderRadius:"50%", width:24, height:24, border:`1px solid ${BORDER2}`, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer" },
  verifiedBadge: { position:"absolute", top:-2, left:-2, width:22, height:22, borderRadius:"50%", backgroundColor:"#fff", border:`1px solid ${HEART.gold}`, display:"flex", alignItems:"center", justifyContent:"center" },
  userName:      { color:DARK, fontSize:18, fontWeight:600, marginBottom:2, fontFamily:"'Cormorant Garamond', serif", letterSpacing:"0.04em" },
  userEmail:     { color:MUTED, fontSize:11, fontWeight:400, marginBottom:8, letterSpacing:"0.03em" },
  pillVerified:  { display:"inline-flex", alignItems:"center", gap:5, borderRadius:999, padding:"3px 10px", backgroundColor:HEART.redSoft, border:`1px solid ${HEART.red}`, marginBottom:4 },
  pillPending:   { display:"inline-flex", alignItems:"center", gap:5, borderRadius:999, padding:"3px 10px", backgroundColor:HEART.goldSoft, border:`1px solid ${BORDER}`, marginBottom:4 },
  refundCard:    { backgroundColor:HEART.goldSoft, border:`1px solid ${BORDER}`, borderRadius:14, padding:16, display:"flex", alignItems:"center", justifyContent:"space-between" },
  refundTitle:   { color:DARK, fontWeight:600, fontSize:13, marginBottom:3, letterSpacing:"0.03em" },
  refundSub:     { color:MUTED, fontSize:11, lineHeight:"16px" },
  body:          { padding:"24px 16px 40px" },
  bannerRow:     { display:"flex", gap:12, marginBottom:16 },
  bannerCard:    { flex:1, backgroundColor:SURF2, borderRadius:14, padding:14, display:"flex", alignItems:"center", gap:12, border:`1px solid ${BORDER}`, cursor:"pointer", textAlign:"left" },
  bannerIcon:    { width:38, height:38, backgroundColor:HEART.goldSoft, borderRadius:12, display:"flex", alignItems:"center", justifyContent:"center", border:`1px solid ${BORDER}` },
  bannerSub:     { fontSize:10, color:MUTED, marginBottom:2, letterSpacing:"0.03em" },
  bannerAction:  { fontSize:12, fontWeight:600, color:HEART.red, letterSpacing:"0.03em" },
  actionRow:     { display:"flex", gap:10, marginBottom:22 },
  sectionTitle:  { fontSize:10, fontWeight:600, color:MUTED, marginBottom:12, letterSpacing:"0.2em" },
  addressCard:   { backgroundColor:SURF2, borderRadius:14, padding:22, textAlign:"center", marginBottom:14, border:`1px solid ${BORDER}` },
  addAddressBtn: { border:"none", borderRadius:999, padding:"10px 24px", background:`linear-gradient(135deg, ${HEART.red}, ${HEART.redDark})`, cursor:"pointer", color:"#fff", fontWeight:600, fontSize:12, display:"flex", alignItems:"center", justifyContent:"center", letterSpacing:"0.08em", fontFamily:"'Montserrat', sans-serif" },
  listItem:      { backgroundColor:SURF2, borderRadius:14, padding:"14px 16px", display:"flex", alignItems:"center", marginBottom:8, width:"100%", border:`1px solid ${BORDER}`, cursor:"pointer", textAlign:"left", fontFamily:"inherit" },
  listIconWrap:  { width:38, height:38, borderRadius:12, backgroundColor:HEART.goldSoft, border:`1px solid ${BORDER}`, display:"flex", alignItems:"center", justifyContent:"center", marginRight:14, flexShrink:0 },
  listLabel:     { fontSize:13, fontWeight:600, color:DARK },
  supportBtn:    { backgroundColor:HEART.red, borderRadius:"50%", width:36, height:36, display:"flex", alignItems:"center", justifyContent:"center", marginLeft:"auto" },
  logoutBtn:     { width:"100%", borderRadius:999, padding:"16px 16px", background:`linear-gradient(135deg, ${HEART.red}, ${HEART.redDark})`, border:"none", cursor:"pointer", color:"#fff", fontWeight:700, fontSize:12, display:"flex", alignItems:"center", justifyContent:"center", marginBottom:8, letterSpacing:"0.2em", fontFamily:"'Montserrat', sans-serif" },
  referralCard:  { backgroundColor:SURF2, borderRadius:14, padding:20, border:`1px solid ${BORDER}`, marginTop:12, marginBottom:18 },
  primaryBtn:    { background:`linear-gradient(135deg, ${HEART.red}, ${HEART.redDark})`, border:"none", color:"#fff", fontWeight:700, borderRadius:999, padding:"14px 40px", cursor:"pointer", fontSize:12, letterSpacing:"0.2em", fontFamily:"'Montserrat', sans-serif" },
};

/* ── Desktop styles ─────────────────────────────────────────── */
const D: { [k: string]: React.CSSProperties } = {
  root:          { minHeight:"100vh", backgroundColor:BG, fontFamily:"'Montserrat', 'Cormorant Garamond', sans-serif" },
  nav:           { position:"sticky", top:0, zIndex:100, background:SURFACE, borderBottom:`1px solid ${BORDER}`, display:"flex", alignItems:"center", justifyContent:"space-between", padding:"0 40px", height:64 },
  navIconBtn:    { width:38, height:38, borderRadius:10, background:"transparent", border:`1px solid ${BORDER}`, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center" },

  heroBanner:    { position:"relative", overflow:"hidden", background:SURFACE, padding:"52px 56px 48px", display:"flex", alignItems:"center", justifyContent:"space-between", gap:48, borderBottom:`1px solid ${BORDER}` },
  heroLeft:      { display:"flex", alignItems:"center", gap:28 },
  heroAvatarOuter: { position:"relative", width:110, height:110, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 },
  heroAvatar:            { width:96, height:96, borderRadius:"50%", border:`1.5px solid ${HEART.gold}`, objectFit:"cover", display:"block" },
  heroAvatarPlaceholder: { width:96, height:96, borderRadius:"50%", backgroundColor:HEART.goldSoft, border:`1.5px solid ${HEART.gold}`, display:"flex", alignItems:"center", justifyContent:"center" },
  heroInitials:  { color:HEART.red, fontSize:30, fontWeight:600, fontFamily:"'Cormorant Garamond', serif" },
  heroCamBtn:    { position:"absolute", bottom:4, right:4, backgroundColor:"#fff", borderRadius:"50%", width:30, height:30, border:`1px solid ${BORDER2}`, display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer" },
  heroVerBadge:  { position:"absolute", top:-2, left:-2, width:26, height:26, borderRadius:"50%", backgroundColor:"#fff", border:`1px solid ${HEART.gold}`, display:"flex", alignItems:"center", justifyContent:"center" },
  heroName:      { color:DARK, fontSize:32, fontWeight:600, letterSpacing:"0.04em", fontFamily:"'Cormorant Garamond', serif" },
  heroEmail:     { color:MUTED, fontSize:12, marginTop:4, letterSpacing:"0.03em" },
  heroRight:     { display:"flex", flexDirection:"column", gap:16, flex:1, maxWidth:580 },
  heroToggleCard:{ backgroundColor:HEART.goldSoft, border:`1px solid ${BORDER}`, borderRadius:14, padding:"18px 24px", display:"flex", alignItems:"center", justifyContent:"space-between" },
  heroStats:     { display:"flex", gap:12 },

  grid:          { display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:32, padding:"44px 56px 64px", maxWidth:1400, margin:"0 auto" },
  col:           { display:"flex", flexDirection:"column" },
  colHeader:     { marginBottom:18, paddingBottom:14, borderBottom:`1px solid ${BORDER}` },
  colTitle:      { color:MUTED, fontSize:10, fontWeight:600, letterSpacing:"0.2em", fontFamily:"'Montserrat', sans-serif" },

  listItem:      { backgroundColor:SURF2, border:`1px solid ${BORDER}`, borderRadius:14, padding:"14px 16px", display:"flex", alignItems:"center", marginBottom:8, width:"100%", cursor:"pointer", textAlign:"left", transition:"border-color 0.2s", fontFamily:"inherit" },
  listIconWrap:  { width:34, height:34, borderRadius:10, backgroundColor:HEART.goldSoft, border:`1px solid ${BORDER}`, display:"flex", alignItems:"center", justifyContent:"center", marginRight:12, flexShrink:0, color:HEART.red },
  logoutBtn:     { marginTop:16, borderRadius:999, padding:"14px 16px", background:`linear-gradient(135deg, ${HEART.red}, ${HEART.redDark})`, border:"none", cursor:"pointer", color:"#fff", fontWeight:700, fontSize:12, display:"flex", alignItems:"center", gap:10, justifyContent:"center", letterSpacing:"0.2em", fontFamily:"'Montserrat', sans-serif" },
  referralCard:  { backgroundColor:SURF2, borderRadius:14, padding:20, border:`1px solid ${BORDER}`, marginTop:4 },
};